import React, { useState, useEffect } from 'react';
import {
  Settings,
  Info,
  Loader2,
  RotateCcw,
  Save,
  ShieldCheck,
  Zap,
  Clock,
  Hash,
  Globe,
  QrCode,
} from 'lucide-react';
import useQRConfig from '../../hooks/useQRConfig';

function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed" />
    </label>
  );
}

function SectionCard({ icon: Icon, iconBg, iconColor, title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 bg-slate-50/50">
        <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon size={14} className={iconColor} />
        </div>
        <div>
          <h3 className="text-xs font-semibold text-slate-900">{title}</h3>
          {description && <p className="text-[10px] text-slate-400 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <label className="block text-xs font-medium text-slate-700">{label}</label>
        {description && <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

export default function QRSettings() {
  const {
    config,
    loading,
    saveConfig,
    toggleOrdering,
    resetDefaults,
  } = useQRConfig();

  const [form, setForm] = useState({
    requireStaffApproval: false,
    autoActivateSession: true,
    sessionTimeoutHours: 2,
    maxOrdersPerSession: 10,
    baseUrl: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        requireStaffApproval: config.requireStaffApproval ?? false,
        autoActivateSession: config.autoActivateSession ?? true,
        sessionTimeoutHours: config.sessionTimeoutHours ?? 2,
        maxOrdersPerSession: config.maxOrdersPerSession ?? 10,
        baseUrl: config.baseUrl ?? '',
      });
    }
  }, [config]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveConfig(form);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    resetDefaults();
    setForm({
      requireStaffApproval: false,
      autoActivateSession: true,
      sessionTimeoutHours: 2,
      maxOrdersPerSession: 10,
      baseUrl: form.baseUrl,
    });
  };

  if (loading) {
    return (
      <div className="p-6 w-full space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-8 bg-slate-200 rounded-lg animate-pulse" />
          <div className="h-6 w-48 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="h-20 bg-white rounded-xl border border-slate-200 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-3" />
              <div className="h-10 w-full bg-slate-100 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header + Actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Settings size={20} className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">QR Menu Settings</h1>
            <p className="text-xs text-slate-500">Configure QR ordering behavior for your restaurant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw size={13} />
            Reset Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* QR Ordering — Prominent Full-Width Toggle */}
      <div className={`rounded-xl border-2 p-5 mb-5 transition-all ${
        config.qrOrderingEnabled
          ? 'border-teal-200 bg-teal-50/40'
          : 'border-slate-200 bg-white'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              config.qrOrderingEnabled ? 'bg-teal-100' : 'bg-slate-100'
            }`}>
              <QrCode size={20} className={config.qrOrderingEnabled ? 'text-teal-600' : 'text-slate-400'} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">QR Ordering</h3>
              <p className="text-xs text-slate-500 mt-0.5">Allow customers to scan & order directly from their table</p>
            </div>
          </div>
          <Toggle
            checked={config.qrOrderingEnabled}
            onChange={() => toggleOrdering()}
          />
        </div>
        {!config.qrOrderingEnabled && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              QR ordering is currently disabled. Customers will not be able to place orders via QR code.
            </p>
          </div>
        )}
      </div>

      {/* Settings Content — greyed out when QR ordering is disabled */}
      <div className={`transition-all ${!config.qrOrderingEnabled ? 'opacity-40 pointer-events-none select-none' : ''}`}>

      {/* Two-Column Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Order Workflow */}
        <SectionCard
          icon={ShieldCheck}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          title="Order Workflow"
          description="How incoming QR orders are handled"
        >
          <div className="space-y-4">
            <SettingRow
              label="Require Staff Approval"
              description="Orders need staff confirmation before going to kitchen"
            >
              <Toggle
                checked={form.requireStaffApproval}
                onChange={(val) => setForm((f) => ({ ...f, requireStaffApproval: val }))}
              />
            </SettingRow>
            <div className="border-t border-slate-100" />
            <SettingRow
              label="Auto Activate Session"
              description="Automatically start a session when QR code is scanned"
            >
              <Toggle
                checked={form.autoActivateSession}
                onChange={(val) => setForm((f) => ({ ...f, autoActivateSession: val }))}
              />
            </SettingRow>
          </div>
        </SectionCard>

        {/* Session Limits */}
        <SectionCard
          icon={Clock}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          title="Session Limits"
          description="Control session duration and order caps"
        >
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Clock size={12} className="text-slate-400" />
                <label className="text-xs font-medium text-slate-700">Session Timeout</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={24}
                  value={form.sessionTimeoutHours}
                  onChange={(e) => setForm((f) => ({ ...f, sessionTimeoutHours: Math.max(1, Math.min(24, parseInt(e.target.value) || 1)) }))}
                  className="w-20 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-center focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
                />
                <span className="text-xs text-slate-500">hours of inactivity</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Session auto-expires after this duration (1–24 hrs)</p>
            </div>
            <div className="border-t border-slate-100" />
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Hash size={12} className="text-slate-400" />
                <label className="text-xs font-medium text-slate-700">Max Orders Per Session</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={form.maxOrdersPerSession}
                  onChange={(e) => setForm((f) => ({ ...f, maxOrdersPerSession: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) }))}
                  className="w-20 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-center focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
                />
                <span className="text-xs text-slate-500">orders per session</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Prevents abuse — customer is capped at this limit (1–50)</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* QR Code URL — Full Width */}
      <SectionCard
        icon={Globe}
        iconBg="bg-purple-100"
        iconColor="text-purple-600"
        title="QR Code URL"
        description="Base URL used when generating QR codes for tables"
      >
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">Base URL</label>
          <input
            type="text"
            value={form.baseUrl}
            onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
            placeholder="https://menu.yourdomain.com"
            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
          />
          <p className="text-[10px] text-slate-400 mt-1.5">Each table's QR code will be generated as <span className="font-mono text-slate-500">{form.baseUrl || 'https://...'}/qr/&lt;code&gt;</span></p>
        </div>
      </SectionCard>

      </div>{/* end disabled wrapper */}
    </div>
  );
}
